// src/components/pages/blotter/converts/ManualConvertTradeEntry.js
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchConvertStrategies,
  fetchSecurityInfo,
  submitManualConvertTrade,
} from "../../../../services/Axios";
import { formatHedgeTradeError } from "../../../../utils/submitTrades";
import {
  buildManualTradePayload,
  canSubmitTrade,
  getResultSeverity,
} from "../../../../utils/manualConvertTrade";
import { HEDGE_TRADE_STATUS_CONFIG } from "./Converts_Blotter";

// Must match CreateBlotterEntry.js's ACTION_OPTIONS.
const ACTION_OPTIONS = ["Buy", "Sell", "Short", "Cover"];

// Defaults for the Eze-specific fields the backend used to default silently.
// Prefilled here (editable) so existing behavior is the default but a trader
// can override for a different desk/trader/currency.
const DEFAULT_TRADER = "EM";
const DEFAULT_MANAGER = "GCM";
const DEFAULT_CURRENCY = "USD";

// A function, not a module-level constant — trade_date must be computed at
// the moment it's needed (mount / reset), not once when the module first
// loads, or a long-lived tab can show yesterday's date after midnight.
// Matches the pattern in CreateBlotterEntry.js / CopyBlotterEntry.js.
const emptyForm = () => ({
  security: "",
  strategy: "",
  action: "",
  quantity: "",
  price: "",
  broker: "",
  trader: DEFAULT_TRADER,
  manager: DEFAULT_MANAGER,
  currency: DEFAULT_CURRENCY,
  trade_date: new Date().toISOString().split("T")[0],
});

export default function ManualConvertTradeEntry() {
  const [securities, setSecurities] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [securitiesError, setSecuritiesError] = useState("");
  const [strategiesError, setStrategiesError] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Guards against a second submission firing before the disabled state has
  // re-rendered onto the button — a state-only guard can be beaten by a fast
  // double-click since setState doesn't take effect until the next render.
  const submittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      const [securityData, strategyData] = await Promise.all([
        fetchSecurityInfo(),
        fetchConvertStrategies(),
      ]);
      if (cancelled) return;

      // Both fetchers swallow errors and resolve to undefined on failure
      // instead of throwing, so a falsy result is how "the request failed"
      // shows up here.
      if (securityData) {
        // Glz_Security_Info is a typeahead/suggestion source only — traders
        // must be able to submit any symbol, so the full fetched list is
        // passed through unfiltered by type.
        setSecurities(securityData);
      } else {
        setSecuritiesError("Failed to load securities. Try refreshing the page.");
      }

      if (strategyData) {
        setStrategies(strategyData);
      } else {
        setStrategiesError("Failed to load strategies. Try refreshing the page.");
      }

      setLoadingOptions(false);
    }

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.security?.trim?.()) return "Enter a security symbol.";
    if (!form.strategy?.trim?.()) return "Enter a strategy.";
    if (!form.action) return "Select an action.";
    const quantity = Number(form.quantity);
    if (!form.quantity || !(quantity > 0)) return "Quantity must be a positive number.";
    const price = Number(form.price);
    if (!form.price || !(price > 0)) return "Price must be a positive number.";
    if (!form.broker.trim()) return "Broker is required.";
    if (!form.trader.trim()) return "Trader is required.";
    if (!form.manager.trim()) return "Manager is required.";
    if (!form.currency.trim()) return "Currency is required.";
    if (!form.trade_date) return "Trade date is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return;
    // Defense in depth against an Enter-key submit bypassing a disabled
    // Submit button in some browsers — a completed non-FAILED submission
    // must not be resubmittable except via "Enter Another Trade".
    if (!canSubmitTrade(submitResult)) return;

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    setSubmitResult(null);

    try {
      const payload = buildManualTradePayload(form);
      const result = await submitManualConvertTrade(payload);
      setSubmitResult(result);
    } catch (err) {
      // submitManualConvertTrade does not catch its own errors, so a 400
      // validation failure or network error lands here and must not be left
      // as an unhandled rejection.
      console.error("Error submitting manual convert trade:", err);
      const detail = err?.response?.data?.detail;
      setFormError(detail || err.message || "Failed to submit trade.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(emptyForm());
    setFormError("");
    setSubmitResult(null);
  };

  const statusConfig = submitResult
    ? HEDGE_TRADE_STATUS_CONFIG[submitResult.status]
    : null;
  // hedge_trade_error / errors is a Django JSONField (list, dict, or absent)
  // — never render it directly; formatHedgeTradeError flattens it to a
  // plain string first (a raw object here would crash the app inside a
  // Tooltip or Typography child).
  const resultErrorMessage = submitResult
    ? formatHedgeTradeError(submitResult.errors)
    : "";

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 }, maxWidth: 800, mx: "auto", mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Manual Convert Trade Entry
      </Typography>

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError("")}>
          {formError}
        </Alert>
      )}

      {submitResult && (
        <Alert
          severity={getResultSeverity(submitResult.status)}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: resultErrorMessage ? 1 : 0 }}>
            <Typography variant="body2">Trade #{submitResult.trade_id}</Typography>
            {statusConfig ? (
              <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
            ) : (
              <Chip label={submitResult.status} size="small" />
            )}
          </Box>
          {resultErrorMessage && (
            <Typography variant="body2">{resultErrorMessage}</Typography>
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={securities}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt?.SYMBOL || ""
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.SYMBOL === (typeof val === "string" ? val : val?.SYMBOL)
                  }
                  value={form.security}
                  onChange={(_e, val) => {
                    // Fires when an option is clicked (val is the security
                    // object) or on freeSolo Enter (val is already a string,
                    // already handled by onInputChange below).
                    if (val && typeof val === "object") {
                      handleFieldChange("security", val.SYMBOL || "");
                    }
                  }}
                  onInputChange={(_e, val) =>
                    handleFieldChange("security", val ?? "")
                  }
                  disabled={submitting}
                  loading={loadingOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Security"
                      size="small"
                      required
                      error={!!securitiesError}
                      helperText={
                        securitiesError
                          ? `${securitiesError} Suggestions unavailable — you can still type a symbol.`
                          : ""
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={strategies}
                  value={form.strategy}
                  onChange={(_e, val) => handleFieldChange("strategy", val ?? "")}
                  onInputChange={(_e, val) =>
                    handleFieldChange("strategy", val ?? "")
                  }
                  disabled={submitting}
                  loading={loadingOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Strategy"
                      size="small"
                      required
                      error={!!strategiesError}
                      helperText={
                        strategiesError
                          ? `${strategiesError} Suggestions unavailable — you can still type a strategy.`
                          : ""
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required disabled={submitting}>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={form.action}
                    label="Action"
                    onChange={(e) => handleFieldChange("action", e.target.value)}
                  >
                    {ACTION_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Broker"
                  value={form.broker}
                  onChange={(e) => handleFieldChange("broker", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Trader"
                  value={form.trader}
                  onChange={(e) => handleFieldChange("trader", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manager"
                  value={form.manager}
                  onChange={(e) => handleFieldChange("manager", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => handleFieldChange("currency", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  value={form.quantity}
                  onChange={(e) => handleFieldChange("quantity", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Price"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={form.price}
                  onChange={(e) => handleFieldChange("price", e.target.value)}
                  disabled={submitting}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Trade Date"
                  type="date"
                  value={form.trade_date}
                  onChange={(e) => handleFieldChange("trade_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={submitting}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={handleReset} disabled={submitting}>
                {submitResult ? "Enter Another Trade" : "Clear"}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !canSubmitTrade(submitResult)}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                {submitting ? "Submitting..." : "Submit Trade"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
}
